import React, { useState } from "react";
import {
  Globe,
  Upload,
  Layers,
  ChevronDown,
  Check,
  Search,
  Plus,
  Menu,
  X,
  ArrowLeft,
  LogIn,
  User,
  LogOut,
  Tv2,
  Download,
  Settings as SettingsIcon,
  Sparkles,
  LayoutDashboard,
  ShieldCheck,
} from "lucide-react";
import { useI18n } from "../i18n/I18nContext.js";
import { Language } from "../i18n/types.js";
import { useAuth } from "../context/AuthContext.js";
import { VideoItem, SEED_VIDEOS } from "../types/video.js";
import { formatDuration } from "../utils/format.js";
import { NotificationMenu } from "./NotificationMenu.js";
import { BrandLogo } from "./common/BrandLogo.js";

export interface HeaderProps {
  activeTab: string;
  onTabChange: (tab: any) => void;
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  onSearchSubmit?: (e: React.FormEvent) => void;
  onToggleSidebar?: () => void;
  videos?: VideoItem[];
  onSelectVideo?: (video: VideoItem) => void;
  onSelectCreator?: (creatorHandleOrName: string) => void;
}

// Safely highlight matching substring in text
function highlightMatch(text: string, query: string) {
  if (!query || !text) return text;
  const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi");
  const parts = text.split(regex);
  return parts.map((part, i) =>
    regex.test(part) ? (
      <mark key={i} className="bg-emerald-500/30 text-emerald-300 rounded px-0.5 font-bold">
        {part}
      </mark>
    ) : (
      part
    )
  );
}

export const Header: React.FC<HeaderProps> = ({
  activeTab,
  onTabChange,
  searchQuery = "",
  onSearchChange,
  onSearchSubmit,
  onToggleSidebar,
  videos = [],
  onSelectVideo,
  onSelectCreator,
}) => {
  const { t, language, setLanguage, supportedLanguages, currentLanguageOption } = useI18n();
  const { profile, user, channel, hasChannel, setIsAuthModalOpen, setIsCreateChannelModalOpen, signOut } = useAuth();
  const isAdmin = Boolean(
    user && (
      (user as any).role === "ADMIN" ||
      profile?.role === "ADMIN" ||
      user.user_metadata?.role === "ADMIN" ||
      user.app_metadata?.role === "ADMIN"
    )
  );
  const [showLangMenu, setShowLangMenu] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);

  // Real-time search autocomplete state
  const [debouncedQuery, setDebouncedQuery] = useState(searchQuery);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const desktopSearchRef = React.useRef<HTMLDivElement>(null);
  const mobileSearchRef = React.useRef<HTMLDivElement>(null);

  // 300ms debounce hook
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Click outside & Escape key listeners
  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        desktopSearchRef.current &&
        !desktopSearchRef.current.contains(target) &&
        mobileSearchRef.current &&
        !mobileSearchRef.current.contains(target)
      ) {
        setIsDropdownOpen(false);
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  // Filter videos for autocomplete dropdown (up to 6 results)
  const sourceVideos = videos && videos.length > 0 ? videos : SEED_VIDEOS;
  const queryClean = debouncedQuery.trim().toLowerCase();

  // Server-side search cache
  const [remoteResults, setRemoteResults] = useState<{
    videos: VideoItem[];
    channels: Array<{ name: string; handle: string; avatar?: string }>;
  } | null>(null);

  React.useEffect(() => {
    if (!queryClean) {
      setRemoteResults(null);
      return;
    }

    let isMounted = true;
    const envApi = ((import.meta as any).env?.VITE_API_URL as string | undefined)?.trim().replace(/\/$/, "");
    const apiBase = envApi || (typeof window !== "undefined" && window.location.hostname !== "localhost" && window.location.hostname !== "127.0.0.1" ? "https://hp-helix-api.onrender.com" : "");

    fetch(`${apiBase}/api/v1/search?q=${encodeURIComponent(queryClean)}&limit=6`)
      .then((res) => (res.ok ? res.json() : null))
      .then((json) => {
        if (isMounted && json?.success && json.data) {
          setRemoteResults({
            videos: json.data.videos || [],
            channels: (json.data.channels || []).map((c: any) => ({
              name: c.channelName || c.name,
              handle: c.handle,
              avatar: c.avatarUrl || c.avatar,
            })),
          });
        }
      })
      .catch(() => {});

    return () => {
      isMounted = false;
    };
  }, [queryClean]);

  // Filter channels matching query (combining server & local fallback)
  const matchingChannels = React.useMemo(() => {
    if (!queryClean) return [];

    const channelMap = new Map<string, { name: string; handle: string; avatar?: string }>();

    // Seed defaults
    channelMap.set("jovialimusic", {
      name: "Jovial I Music",
      handle: "@jovialimusic",
      avatar: "https://hphelix.com/assets/jovial-avatar.png",
    });
    channelMap.set("gospelhub", {
      name: "Gospel Hub",
      handle: "@gospelhub",
      avatar: "https://images.unsplash.com/photo-1544717305-2782549b5136?w=150&auto=format&fit=crop&q=80",
    });
    channelMap.set("helixcreator", {
      name: "HP – Helix Creator",
      handle: "@helixcreator",
      avatar: "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=150&auto=format&fit=crop&q=80",
    });

    // Merge server channels if available
    if (remoteResults?.channels) {
      remoteResults.channels.forEach((rc) => {
        const slug = rc.handle.replace(/^@/, "").toLowerCase();
        channelMap.set(slug, rc);
      });
    }

    sourceVideos.forEach((v) => {
      if (v.creatorHandle) {
        const slug = v.creatorHandle.replace(/^@/, "").toLowerCase();
        if (!channelMap.has(slug)) {
          channelMap.set(slug, {
            name: v.creatorName || slug,
            handle: v.creatorHandle,
            avatar: v.creatorAvatar,
          });
        }
      }
    });

    return Array.from(channelMap.values())
      .filter(
        (c) =>
          c.name.toLowerCase().includes(queryClean) ||
          c.handle.toLowerCase().includes(queryClean)
      )
      .slice(0, 3);
  }, [sourceVideos, queryClean, remoteResults]);

  // Relevance-scored video search results (combining server & local fallback)
  const searchResults = React.useMemo(() => {
    if (!queryClean) return [];

    // If remote search has results, merge and prioritize them
    const mergedList = remoteResults?.videos && remoteResults.videos.length > 0
      ? [...remoteResults.videos, ...sourceVideos]
      : sourceVideos;

    const seenIds = new Set<string>();
    const uniqueSource: VideoItem[] = [];
    for (const v of mergedList) {
      const vid = v.streamMediaId || v.uid || v.id;
      if (!seenIds.has(vid)) {
        seenIds.add(vid);
        uniqueSource.push(v);
      }
    }

    return uniqueSource
      .map((v) => {
        let score = 0;
        const titleLower = v.title?.toLowerCase() || "";
        const descLower = v.description?.toLowerCase() || "";
        const catLower = v.category?.toLowerCase() || "";
        const creatorLower = v.creatorName?.toLowerCase() || "";
        const handleLower = v.creatorHandle?.toLowerCase() || "";

        if (titleLower === queryClean) score += 100;
        else if (titleLower.startsWith(queryClean)) score += 50;
        else if (titleLower.includes(queryClean)) score += 30;

        if (creatorLower.includes(queryClean) || handleLower.includes(queryClean)) score += 20;
        if (catLower.includes(queryClean)) score += 15;
        if (descLower.includes(queryClean)) score += 10;

        return { video: v, score };
      })
      .filter((item) => item.score > 0)
      .sort((a, b) => b.score - a.score)
      .map((item) => item.video)
      .slice(0, 5);
  }, [sourceVideos, queryClean, remoteResults]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsDropdownOpen(false);
    setIsMobileSearchOpen(false);
    try {
      if (searchQuery.trim()) {
        window.history.pushState({}, "", `/search?q=${encodeURIComponent(searchQuery.trim())}`);
      }
    } catch {}
    if (onSearchSubmit) onSearchSubmit(e);
  };

  const handleResultClick = (video: VideoItem) => {
    setIsDropdownOpen(false);
    setIsMobileSearchOpen(false);
    if (onSelectVideo) {
      onSelectVideo(video);
    }
    onTabChange("player");
    try {
      const vUid = video.streamMediaId || video.uid || video.id;
      window.history.pushState({}, "", `/watch?v=${vUid}`);
    } catch {}
  };

  const handleChannelClick = (handle: string) => {
    setIsDropdownOpen(false);
    setIsMobileSearchOpen(false);
    const cleanHandle = (handle || "").replace(/^@+/, "").trim();
    if (onSelectCreator) {
      onSelectCreator(cleanHandle);
    } else {
      onTabChange("channel");
    }
    try {
      window.history.pushState({}, "", `/channel/@${cleanHandle}`);
    } catch {}
  };

  const shouldShowDropdown = isDropdownOpen && Boolean(queryClean);

  const renderDropdown = () => {
    if (!shouldShowDropdown) return null;

    const hasAnyResults = matchingChannels.length > 0 || searchResults.length > 0;

    return (
      <div className="absolute top-full left-0 right-0 mt-2 bg-zinc-900/95 backdrop-blur-md border border-zinc-800 rounded-2xl shadow-2xl overflow-hidden z-50 animate-in fade-in slide-in-from-top-2 duration-150 text-left">
        <div className="p-2 space-y-1">
          {/* Matching Channels Section */}
          {matchingChannels.map((ch) => (
            <div
              key={ch.handle}
              onClick={() => handleChannelClick(ch.handle)}
              className="flex items-center gap-3 p-2 rounded-xl hover:bg-zinc-800/80 cursor-pointer transition-colors group border border-transparent hover:border-emerald-500/20"
            >
              <div className="w-10 h-10 rounded-full bg-zinc-800 border border-zinc-700 overflow-hidden flex items-center justify-center font-bold text-emerald-400 text-xs shrink-0">
                {ch.avatar ? (
                  <img src={ch.avatar} alt={ch.name} className="w-full h-full object-cover" />
                ) : (
                  <span>{ch.name.substring(0, 2).toUpperCase()}</span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h4 className="text-xs font-bold text-white group-hover:text-emerald-400 transition-colors truncate">
                    {highlightMatch(ch.name, queryClean)}
                  </h4>
                  <span className="px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/30">
                    Channel
                  </span>
                </div>
                <p className="text-[11px] text-zinc-400 font-mono truncate">
                  {highlightMatch(ch.handle, queryClean)}
                </p>
              </div>
            </div>
          ))}

          {/* Matching Videos Section */}
          {searchResults.map((result) => (
            <div
              key={result.id}
              onClick={() => handleResultClick(result)}
              className="flex items-center gap-3 p-2 rounded-xl hover:bg-zinc-800/80 cursor-pointer transition-colors group"
            >
              {/* 16:9 Micro-Thumbnail */}
              <div className="relative aspect-video w-16 sm:w-20 rounded-lg overflow-hidden bg-zinc-950 shrink-0 border border-zinc-800">
                <img
                  src={
                    result.custom_thumbnail ||
                    result.meta?.customThumbnail ||
                    result.thumbnailUrl ||
                    result.thumbnail
                  }
                  alt={result.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = "/fallback-thumb.svg";
                  }}
                />
                <div className="absolute bottom-0.5 right-0.5 bg-black/80 text-white font-mono text-[8px] font-bold px-1 rounded">
                  {formatDuration(result.duration)}
                </div>
              </div>

              {/* Info */}
              <div className="min-w-0 flex-1 space-y-0.5">
                <h4 className="text-xs font-semibold text-white group-hover:text-emerald-400 transition-colors line-clamp-1">
                  {highlightMatch(result.title, queryClean)}
                </h4>
                <div className="flex items-center gap-2 text-[11px] text-zinc-400">
                  <span className="truncate">{result.creatorName}</span>
                  <span>•</span>
                  <span className="text-emerald-400/80 font-mono text-[10px] bg-emerald-500/10 px-1.5 py-0.2 rounded-full border border-emerald-500/20">
                    {result.category}
                  </span>
                </div>
              </div>
            </div>
          ))}

          {!hasAnyResults && (
            <div className="px-4 py-3 text-xs text-zinc-500 text-center font-sans">
              No matching videos or channels found for &ldquo;{debouncedQuery}&rdquo;
            </div>
          )}
        </div>

        {/* Bottom Action Footer */}
        <div
          onClick={handleSearchSubmit}
          className="px-4 py-2.5 bg-zinc-950/90 border-t border-zinc-800/80 flex items-center justify-between text-xs text-zinc-400 hover:text-white hover:bg-zinc-800/60 cursor-pointer transition-colors"
        >
          <div className="flex items-center gap-2">
            <Search className="w-3.5 h-3.5 text-emerald-400" />
            <span>
              Press{" "}
              <kbd className="px-1.5 py-0.5 rounded bg-zinc-800 border border-zinc-700 font-mono text-[10px] text-zinc-300">
                Enter
              </kbd>{" "}
              to view all results
            </span>
          </div>
          <span className="text-emerald-400 text-[11px] font-semibold flex items-center gap-0.5">
            Search &rarr;
          </span>
        </div>
      </div>
    );
  };

  return (
    <header className="sticky top-0 z-40 bg-zinc-950/95 backdrop-blur-xl border-b border-zinc-800/80 font-sans shadow-xl select-none">
      {/* 1. Mobile Full-Width Search Overlay Bar (Active when isMobileSearchOpen is true) */}
      {isMobileSearchOpen ? (
        <div
          ref={mobileSearchRef}
          className="relative w-full px-3 h-14 flex sm:hidden items-center gap-2 bg-zinc-950 animate-in fade-in slide-in-from-top-2 duration-150"
        >
          <button
            onClick={() => {
              setIsMobileSearchOpen(false);
              setIsDropdownOpen(false);
            }}
            className="p-2 rounded-xl text-zinc-400 hover:text-white hover:bg-zinc-900 transition-colors"
            title="Close Search"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <form onSubmit={handleSearchSubmit} className="flex-1 flex items-center">
            <div className="relative w-full flex items-center bg-zinc-900 border border-zinc-800 rounded-full focus-within:border-emerald-500/80 focus-within:ring-2 focus-within:ring-emerald-500/20 transition-all shadow-inner">
              <Search className="w-4 h-4 text-zinc-400 ml-3.5 pointer-events-none" />
              <input
                type="text"
                autoFocus
                value={searchQuery}
                onFocus={() => {
                  setIsFocused(true);
                  setIsDropdownOpen(true);
                }}
                onChange={(e) => {
                  if (onSearchChange) onSearchChange(e.target.value);
                  setIsDropdownOpen(true);
                }}
                placeholder={t.player.enterHlsOrUid}
                className="w-full bg-transparent px-3 py-2 text-xs text-white placeholder:text-zinc-500 focus:outline-none font-sans"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => {
                    if (onSearchChange) onSearchChange("");
                    setIsDropdownOpen(false);
                  }}
                  className="p-1 text-zinc-500 hover:text-zinc-300"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
              <button
                type="submit"
                className="px-3.5 py-1.5 mr-1 rounded-full bg-emerald-500 text-zinc-950 text-xs font-bold transition-all"
              >
                Go
              </button>
            </div>
          </form>

          {/* Mobile Autocomplete Dropdown */}
          {renderDropdown()}
        </div>
      ) : (
        /* 2. Standard Header Bar */
        <div className="w-full px-3 sm:px-6 h-14 sm:h-16 flex items-center justify-between gap-2 sm:gap-4">
          {/* Left: Sidebar Hamburger + Brand Logo */}
          <div className="flex items-center gap-2 sm:gap-4 shrink-0">
            {/* Hamburger Menu Button */}
            <button
              onClick={onToggleSidebar}
              className="p-2 rounded-xl text-zinc-400 hover:text-white hover:bg-zinc-900 transition-colors"
              title="Toggle Menu"
            >
              <Menu className="w-5 h-5" />
            </button>

            {/* Brand Logo */}
            <BrandLogo
              variant="full"
              size="md"
              badgeText="v1.0"
              onClick={() => onTabChange("home")}
            />
          </div>

          {/* Center: Desktop Search Bar (Hidden on Mobile < sm / 640px) */}
          <div ref={desktopSearchRef} className="hidden sm:flex flex-1 max-w-md mx-2 sm:mx-4 relative">
            <form onSubmit={handleSearchSubmit} className="relative w-full flex items-center">
              <div className="relative w-full flex items-center bg-zinc-900/90 border border-zinc-800 rounded-full focus-within:border-emerald-500/80 focus-within:ring-2 focus-within:ring-emerald-500/20 transition-all shadow-inner">
                <Search className="w-4 h-4 text-zinc-400 ml-3.5 pointer-events-none" />
                <input
                  type="text"
                  value={searchQuery}
                  onFocus={() => {
                    setIsFocused(true);
                    setIsDropdownOpen(true);
                  }}
                  onChange={(e) => {
                    if (onSearchChange) onSearchChange(e.target.value);
                    setIsDropdownOpen(true);
                  }}
                  placeholder={t.player.enterHlsOrUid}
                  className="w-full bg-transparent px-3 py-2 text-xs text-white placeholder:text-zinc-500 focus:outline-none font-sans"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => {
                      if (onSearchChange) onSearchChange("");
                      setIsDropdownOpen(false);
                    }}
                    className="p-1 text-zinc-500 hover:text-zinc-300 mr-1"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
                <button
                  type="submit"
                  className="px-3.5 py-1.5 mr-1 rounded-full bg-zinc-800 hover:bg-emerald-500 hover:text-zinc-950 text-zinc-300 text-xs font-semibold transition-all flex items-center gap-1"
                  title={t.player.loadVideo}
                >
                  <Search className="w-3 h-3" />
                </button>
              </div>
            </form>

            {/* Desktop Autocomplete Dropdown */}
            {renderDropdown()}
          </div>

          {/* Right: Mobile Search Button + Language Switcher + Upload + Profile Avatar */}
          <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
            {/* Mobile Search Button (Visible only on < sm / 640px) */}
            <button
              onClick={() => setIsMobileSearchOpen(true)}
              className="p-2 rounded-xl text-zinc-400 hover:text-white hover:bg-zinc-900 sm:hidden transition-colors"
              title="Search"
            >
              <Search className="w-4 h-4" />
            </button>

            {/* Multilingual Switcher Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowLangMenu(!showLangMenu)}
                className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-xl bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-xs text-white font-medium transition-all shadow-sm"
                title="Change Language"
              >
                <Globe className="w-3.5 h-3.5 text-emerald-400" />
                <span>{currentLanguageOption.flag}</span>
                <span className="hidden xl:inline">{currentLanguageOption.nativeName}</span>
                <span className="hidden sm:inline xl:hidden font-mono uppercase text-[11px]">{language}</span>
                <ChevronDown className="w-3 h-3 text-zinc-400" />
              </button>

              {showLangMenu && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setShowLangMenu(false)}
                  />
                  <div className="absolute right-0 mt-2 w-48 bg-zinc-950 border border-zinc-800 rounded-xl py-1.5 shadow-2xl z-50 text-xs">
                    <div className="px-3 py-1 text-[10px] font-mono uppercase text-zinc-500 border-b border-zinc-800/80 mb-1">
                      Select Language
                    </div>
                    {supportedLanguages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => {
                          setLanguage(lang.code as Language);
                          setShowLangMenu(false);
                        }}
                        className={`w-full text-left px-3 py-2 flex items-center justify-between hover:bg-zinc-900 transition-colors ${
                          language === lang.code
                            ? "text-emerald-400 font-bold bg-emerald-500/10"
                            : "text-zinc-300"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <span>{lang.flag}</span>
                          <span>{lang.nativeName}</span>
                          <span className="text-[10px] text-zinc-500 font-mono">({lang.code})</span>
                        </div>
                        {language === lang.code && <Check className="w-3.5 h-3.5 text-emerald-400" />}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Notification Bell Dropdown */}
            <NotificationMenu
              userId={user?.id || profile?.id}
              onNavigate={(url) => {
                if (url.startsWith("/watch/")) {
                  const targetId = url.replace("/watch/", "").trim();
                  if (onSelectVideo && videos) {
                    const match = videos.find((v) => v.id === targetId || v.streamMediaId === targetId || v.uid === targetId);
                    if (match) {
                      onSelectVideo(match);
                      onTabChange("player");
                      return;
                    }
                  }
                }
                window.location.href = url;
              }}
            />

            {/* Upload Button: Full on sm+, compact icon on mobile */}
            <button
              onClick={() => {
                if (!user) {
                  setIsAuthModalOpen(true);
                  return;
                }
                if (!hasChannel) {
                  setIsCreateChannelModalOpen(true);
                  return;
                }
                onTabChange("studio");
              }}
              className={`p-2 sm:px-3.5 sm:py-1.5 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all shadow-md ${
                activeTab === "studio" || activeTab === "upload"
                  ? "bg-emerald-500 text-zinc-950 shadow-emerald-500/20"
                  : "bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white"
              }`}
              title={t.common.upload}
            >
              <Plus className="w-4 h-4 stroke-[3]" />
              <span className="hidden sm:inline">{t.common.upload}</span>
            </button>

            {/* User Profile Avatar / GitHub Login Button */}
            {profile ? (
              <div className="relative shrink-0">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="relative group/avatar cursor-pointer flex items-center focus:outline-none"
                  title={channel?.channel_name || profile.displayName || "Account"}
                >
                  <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-emerald-500 to-teal-500 p-0.5 border border-zinc-700 hover:border-emerald-400 transition-all flex items-center justify-center overflow-hidden shadow-sm">
                    {channel?.avatar_url || profile.avatarUrl ? (
                      <img
                        src={channel?.avatar_url || profile.avatarUrl}
                        alt={channel?.channel_name || profile.displayName}
                        className="w-full h-full object-cover rounded-full"
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    ) : (
                      <span className="text-xs font-bold text-zinc-950">
                        {(channel?.channel_name || profile.displayName).substring(0, 2).toUpperCase()}
                      </span>
                    )}
                  </div>
                  <span className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-emerald-500 ring-2 ring-zinc-950" />
                </button>

                {/* User Dropdown Menu */}
                {showUserMenu && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setShowUserMenu(false)}
                    />
                    <div className="absolute right-0 mt-2 w-56 bg-zinc-950 border border-zinc-800 rounded-2xl p-2 shadow-2xl z-50 text-xs animate-in fade-in zoom-in-95 duration-150">
                      {/* User Info Header */}
                      <div className="px-3 py-2 border-b border-zinc-800/80 mb-1 space-y-0.5">
                        <p className="font-bold text-white truncate">{channel?.channel_name || profile.displayName}</p>
                        <p className="text-[11px] text-zinc-400 truncate font-mono">
                          @{channel?.handle || profile.handle}
                        </p>
                      </div>

                      {/* Navigation Items */}
                      {hasChannel ? (
                        <>
                          <button
                            onClick={() => {
                              const raw = channel?.handle || profile.handle || "";
                              const cleanHandle = (raw || "").replace(/^@+/, "").trim();
                              onTabChange("channel");
                              setShowUserMenu(false);
                              try {
                                window.history.pushState({}, "", `/channel/@${cleanHandle}`);
                              } catch {}
                            }}
                            className="w-full text-left px-3 py-2 rounded-xl flex items-center gap-2 text-zinc-300 hover:text-white hover:bg-zinc-900 transition-colors"
                          >
                            <Tv2 className="w-4 h-4 text-emerald-400" />
                            <span>{t.nav.viewChannel}</span>
                          </button>

                          <button
                            onClick={() => {
                              onTabChange("studio");
                              setShowUserMenu(false);
                              try {
                                window.history.pushState({}, "", "/studio");
                              } catch {}
                            }}
                            className="w-full text-left px-3 py-2 rounded-xl flex items-center gap-2 text-zinc-300 hover:text-white hover:bg-zinc-900 transition-colors"
                          >
                            <LayoutDashboard className="w-4 h-4 text-emerald-400" />
                            <span>{t.nav.studio}</span>
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => {
                            setShowUserMenu(false);
                            setIsCreateChannelModalOpen(true);
                          }}
                          className="w-full text-left px-3 py-2 rounded-xl flex items-center gap-2 text-emerald-400 hover:bg-emerald-500/10 transition-colors font-bold"
                        >
                          <Sparkles className="w-4 h-4 text-emerald-400" />
                          <span>Create a channel</span>
                        </button>
                      )}

                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          if (!hasChannel) {
                            setIsCreateChannelModalOpen(true);
                            return;
                          }
                          onTabChange("upload");
                        }}
                        className="w-full text-left px-3 py-2 rounded-xl flex items-center gap-2 text-zinc-300 hover:text-white hover:bg-zinc-900 transition-colors"
                      >
                        <Upload className="w-4 h-4 text-emerald-400" />
                        <span>{t.nav.uploadVideo}</span>
                      </button>

                      <button
                        onClick={() => {
                          onTabChange("settings");
                          setShowUserMenu(false);
                          try {
                            window.history.pushState({}, "", "/settings");
                          } catch {}
                        }}
                        className="w-full text-left px-3 py-2 rounded-xl flex items-center gap-2 text-zinc-300 hover:text-white hover:bg-zinc-900 transition-colors"
                      >
                        <SettingsIcon className="w-4 h-4 text-emerald-400" />
                        <span>{t.nav.settings}</span>
                      </button>

                      <button
                        onClick={() => {
                          onTabChange("offline");
                          setShowUserMenu(false);
                        }}
                        className="w-full text-left px-3 py-2 rounded-xl flex items-center gap-2 text-zinc-300 hover:text-white hover:bg-zinc-900 transition-colors"
                      >
                        <Download className="w-4 h-4 text-emerald-400" />
                        <span>{t.nav.offlineLibrary}</span>
                      </button>

                      {/* Admin Portal (Strictly visible only for role ADMIN) */}
                      {isAdmin && (
                        <button
                          onClick={() => {
                            onTabChange("admin");
                            setShowUserMenu(false);
                            try {
                              window.history.pushState({}, "", "/admin");
                            } catch {}
                          }}
                          className="w-full text-left px-3 py-2 rounded-xl flex items-center gap-2 text-emerald-400 hover:bg-emerald-500/10 transition-colors font-semibold"
                        >
                          <ShieldCheck className="w-4 h-4 text-emerald-400" />
                          <span>{t.nav.adminPortal}</span>
                        </button>
                      )}

                      {/* Divider & Sign Out */}
                      <div className="pt-1 border-t border-zinc-800/80 mt-1">
                        <button
                          onClick={() => {
                            signOut();
                            setShowUserMenu(false);
                          }}
                          className="w-full text-left px-3 py-2 rounded-xl flex items-center gap-2 text-rose-400 hover:bg-rose-500/10 transition-colors font-semibold"
                        >
                          <LogOut className="w-4 h-4" />
                          <span>{t.nav.signOut}</span>
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ) : (
              /* Sign In Button when logged out */
              <button
                onClick={() => setIsAuthModalOpen(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 hover:border-emerald-500/50 text-xs font-bold text-white transition-all shadow-sm active:scale-95 shrink-0 group"
                title="Sign In"
              >
                <LogIn className="w-3.5 h-3.5 text-emerald-400 group-hover:text-emerald-300 transition-colors" />
                <span className="hidden sm:inline">Sign In</span>
              </button>
            )}
          </div>
        </div>
      )}
    </header>
  );
};
